import React, { useEffect, useMemo, useState } from 'react';
import { acceptClientAgreement, createClientStripeOnboardingLink, getClientAgreement } from '../../api/clientPortal';
import { useClientAuth } from '../../contexts/ClientAuthContext';
import { useClientTheme } from '../../contexts/ClientThemeContext';
import { ErrorBanner, Spinner } from './ClientDashboardPage';

const formatMoney = (cents) => new Intl.NumberFormat('en-US', {
  style: 'currency',
  currency: 'USD',
}).format(Number(cents || 0) / 100);

export default function ClientAgreementPage() {
  const { theme: T } = useClientTheme();
  const { clientUser, clientBrand } = useClientAuth();
  const [agreement, setAgreement] = useState(null);
  const [launch, setLaunch] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [stripeLoading, setStripeLoading] = useState(false);
  const [signerName, setSignerName] = useState(clientUser?.name || '');
  const [signerEmail, setSignerEmail] = useState(clientUser?.email || '');
  const [signerTitle, setSignerTitle] = useState('');
  const [acceptedTerms, setAcceptedTerms] = useState(false);

  useEffect(() => {
    let active = true;
    getClientAgreement()
      .then((data) => {
        if (!active) return;
        const nextAgreement = data?.agreement || null;
        setAgreement(nextAgreement);
        setLaunch(data?.launch || null);
        setSignerName(nextAgreement?.signerName || clientUser?.name || '');
        setSignerEmail(nextAgreement?.signerEmail || clientUser?.email || '');
        setSignerTitle(nextAgreement?.signerTitle || '');
      })
      .catch((err) => {
        if (active) setError(err?.response?.data?.error || 'Failed to load launch agreement.');
      })
      .finally(() => {
        if (active) setLoading(false);
      });
    return () => { active = false; };
  }, [clientUser?.email, clientUser?.name]);

  const card = useMemo(() => ({
    borderRadius: 10,
    border: `1px solid ${T.border}`,
    background: T.surface,
    padding: 18,
  }), [T.border, T.surface]);

  const inputStyle = {
    width: '100%',
    boxSizing: 'border-box',
    borderRadius: 8,
    border: `1px solid ${T.inputBorder}`,
    background: T.inputBg,
    color: T.inputColor,
    padding: '10px 12px',
    fontSize: 13,
    outline: 'none',
    marginTop: 6,
  };

  const submitAcceptance = async () => {
    if (!agreement?.id) return;
    setSaving(true);
    setError('');
    try {
      const data = await acceptClientAgreement(agreement.id, {
        signerName,
        signerEmail,
        signerTitle,
        acceptedTerms,
      });
      setAgreement(data?.agreement || null);
    } catch (err) {
      setError(err?.response?.data?.error || 'Failed to accept agreement.');
    } finally {
      setSaving(false);
    }
  };

  const startStripeConnect = async () => {
    setStripeLoading(true);
    setError('');
    try {
      const data = await createClientStripeOnboardingLink();
      if (!data?.url) throw new Error('Stripe did not return an onboarding link.');
      window.location.assign(data.url);
    } catch (err) {
      setError(err?.response?.data?.error || err?.message || 'Failed to open Stripe Connect.');
      setStripeLoading(false);
    }
  };

  if (loading) return <Spinner T={T} />;

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12, alignItems: 'flex-start', marginBottom: 20 }}>
        <div>
          <div style={{ fontSize: 20, fontWeight: 700, color: T.text }}>Launch</div>
          <div style={{ fontSize: 13, color: T.textSub, marginTop: 4 }}>{clientBrand?.name || 'Brand'} readiness, payment setup, and agreement</div>
        </div>
        {launch ? (
          <span style={{
            borderRadius: 999,
            border: `1px solid ${launch.ready ? 'rgba(34,197,94,0.42)' : T.border}`,
            color: launch.ready ? '#22c55e' : T.textSub,
            padding: '5px 10px',
            fontSize: 11,
            fontWeight: 800,
            textTransform: 'uppercase',
            letterSpacing: '0.08em',
          }}>
            {launch.statusLabel}
          </span>
        ) : null}
      </div>

      {error ? <div style={{ marginBottom: 14 }}><ErrorBanner T={T}>{error}</ErrorBanner></div> : null}

      {launch ? (
        <section style={{ ...card, marginBottom: 14 }}>
          <div style={{ fontSize: 16, fontWeight: 800, color: T.text }}>Launch Readiness</div>
          <div style={{ fontSize: 13, color: T.textSub, lineHeight: 1.55, marginTop: 6 }}>
            {launch.ready
              ? 'This brand is cleared on the current readiness checks.'
              : 'These are the items Studio Sivad is tracking before launch is fully cleared.'}
          </div>
          <div style={{ display: 'grid', gap: 9, marginTop: 14 }}>
            {(launch.checks || []).map((check) => (
              <div key={check.id} style={{ display: 'grid', gridTemplateColumns: 'auto minmax(0, 1fr)', gap: 10, alignItems: 'flex-start', borderRadius: 8, border: `1px solid ${T.border}`, padding: 11 }}>
                <span style={{
                  width: 22,
                  height: 22,
                  borderRadius: '50%',
                  display: 'grid',
                  placeItems: 'center',
                  fontSize: 12,
                  fontWeight: 900,
                  background: check.status === 'complete' ? 'rgba(34,197,94,0.14)' : check.status === 'not_required' ? 'rgba(148,163,184,0.16)' : 'rgba(201,168,76,0.14)',
                  color: check.status === 'complete' ? '#22c55e' : check.status === 'not_required' ? T.textMuted : T.gold,
                }}>
                  {check.status === 'complete' ? '✓' : check.status === 'not_required' ? '-' : '!'}
                </span>
                <div>
                  <div style={{ fontSize: 13, fontWeight: 800, color: T.text }}>{check.label}</div>
                  <div style={{ fontSize: 12, color: T.textSub, marginTop: 3, lineHeight: 1.45 }}>{check.detail}</div>
                  {check.id === 'stripe_connect' && ['missing', 'action_required'].includes(check.status) ? (
                    <button
                      type="button"
                      onClick={startStripeConnect}
                      disabled={stripeLoading}
                      style={{
                        marginTop: 9,
                        borderRadius: 7,
                        border: `1px solid ${T.gold}`,
                        background: T.gold,
                        color: '#101010',
                        padding: '7px 10px',
                        fontSize: 12,
                        fontWeight: 850,
                        cursor: stripeLoading ? 'wait' : 'pointer',
                        opacity: stripeLoading ? 0.68 : 1,
                      }}
                    >
                      {stripeLoading ? 'Opening Stripe...' : 'Connect Stripe'}
                    </button>
                  ) : null}
                </div>
              </div>
            ))}
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: 10, marginTop: 14 }}>
            <Metric label="Stripe Status" value={launch.stripeConnect?.statusLabel || 'Unknown'} T={T} />
            <Metric label="Storefront" value={launch.storefrontPublished ? 'Published' : 'Not published'} T={T} />
          </div>
          {launch.storefrontUrl ? (
            <a href={launch.storefrontUrl} target="_blank" rel="noreferrer" style={{ display: 'inline-flex', marginTop: 12, color: T.gold, fontSize: 13, fontWeight: 800, textDecoration: 'none' }}>
              Open storefront
            </a>
          ) : null}
        </section>
      ) : null}

      {!agreement ? (
        <div style={card}>
          <div style={{ fontSize: 14, color: T.text, fontWeight: 700, marginBottom: 6 }}>No agreement is ready yet.</div>
          <div style={{ fontSize: 13, color: T.textSub, lineHeight: 1.55 }}>
            Studio Sivad has not sent a launch authorization for this brand. Once it is sent, it will appear here for review.
          </div>
        </div>
      ) : (
        <div style={{ display: 'grid', gap: 14 }}>
          <section style={card}>
            <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12, alignItems: 'flex-start', flexWrap: 'wrap' }}>
              <div>
                <div style={{ fontSize: 16, fontWeight: 800, color: T.text }}>{agreement.termsSnapshot?.title || 'Studio Sivad Agreement'}</div>
                <div style={{ fontSize: 12, color: T.textMuted, marginTop: 4 }}>Version {agreement.version || '1.0'}</div>
              </div>
              <span style={{
                borderRadius: 999,
                border: `1px solid ${agreement.status === 'accepted' ? 'rgba(34,197,94,0.42)' : T.border}`,
                color: agreement.status === 'accepted' ? '#22c55e' : T.gold,
                padding: '5px 10px',
                fontSize: 11,
                fontWeight: 800,
                textTransform: 'uppercase',
                letterSpacing: '0.08em',
              }}>
                {agreement.status}
              </span>
            </div>
            <div style={{ fontSize: 13, color: T.textSub, lineHeight: 1.55, marginTop: 10 }}>
              {agreement.termsSnapshot?.body}
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))', gap: 10, marginTop: 18 }}>
              <Metric label="Brand Legal Name" value={agreement.brandLegalName || clientBrand?.name || '-'} T={T} />
              <Metric label="Preorder Sales Fee" value={`${Number(agreement.platformFeePercent || 0)}%${Number(agreement.platformFeeFixedCents || 0) ? ` + ${formatMoney(agreement.platformFeeFixedCents)}` : ''}`} T={T} />
              <Metric label="Sample / Bulk Order Fee" value={`${Number(agreement.backendOrderFeePercent ?? agreement.platformFeePercent ?? 0)}%`} T={T} />
              <Metric label="Payment Flow" value={agreement.merchantOfRecordModel === 'connected_brand' ? 'Brand Stripe Connect' : 'Platform payout'} T={T} />
              <Metric label="Tax Responsibility" value={agreement.taxResponsibility === 'platform' ? 'Studio Sivad' : 'Brand'} T={T} />
            </div>
          </section>

          {agreement.termsSnapshot?.summaryBullets?.length ? (
            <section style={card}>
              <div style={{ fontSize: 14, fontWeight: 800, color: T.text, marginBottom: 10 }}>Plain-English Summary</div>
              <div style={{ display: 'grid', gap: 9 }}>
                {agreement.termsSnapshot.summaryBullets.map((item) => (
                  <div key={item} style={{ display: 'grid', gridTemplateColumns: '18px minmax(0, 1fr)', gap: 9, alignItems: 'flex-start' }}>
                    <span style={{ color: T.gold, fontWeight: 900, lineHeight: 1.35 }}>•</span>
                    <span style={{ fontSize: 13, color: T.textSub, lineHeight: 1.55 }}>{item}</span>
                  </div>
                ))}
              </div>
            </section>
          ) : null}

          <section style={card}>
            <div style={{ fontSize: 14, fontWeight: 800, color: T.text, marginBottom: 6 }}>Full Agreement Terms</div>
            <div style={{ fontSize: 12, color: T.textSub, lineHeight: 1.5 }}>
              These terms are the agreement text being accepted for this drop authorization.
            </div>
          </section>

          {(agreement.termsSnapshot?.sections || []).map((section) => (
            <section key={section.heading} style={card}>
              <div style={{ fontSize: 14, fontWeight: 800, color: T.text, marginBottom: 6 }}>{section.heading}</div>
              <div style={{ fontSize: 13, color: T.textSub, lineHeight: 1.6 }}>{section.body}</div>
            </section>
          ))}

          {agreement.status === 'accepted' ? (
            <section style={card}>
              <div style={{ fontSize: 14, color: '#22c55e', fontWeight: 800 }}>Accepted</div>
              <div style={{ fontSize: 13, color: T.textSub, marginTop: 6 }}>
                Accepted by {agreement.signerName || 'signer'}{agreement.signerTitle ? `, ${agreement.signerTitle}` : ''} {agreement.acceptedAt ? `on ${new Date(agreement.acceptedAt).toLocaleString()}` : ''}.
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: 10, marginTop: 14 }}>
                <Metric label="Signer Email" value={agreement.signerEmail || '-'} T={T} />
                <Metric label="Accepted Version" value={agreement.acceptedTermsVersion || agreement.version || '-'} T={T} />
                <Metric label="IP Fingerprint" value={agreement.acceptedIpFingerprint || '-'} T={T} />
              </div>
            </section>
          ) : (
            <section style={card}>
              <div style={{ fontSize: 14, fontWeight: 800, color: T.text, marginBottom: 6 }}>Accept Agreement</div>
              <div style={{ fontSize: 12, color: T.textSub, lineHeight: 1.5, marginBottom: 12 }}>
                Acceptance records the signer details, timestamp, IP fingerprint, browser user agent, and agreement version.
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: 12 }}>
                <label style={{ fontSize: 12, fontWeight: 700, color: T.text }}>
                  Signer Name
                  <input style={inputStyle} value={signerName} onChange={(event) => setSignerName(event.target.value)} />
                </label>
                <label style={{ fontSize: 12, fontWeight: 700, color: T.text }}>
                  Title / Role
                  <input style={inputStyle} value={signerTitle} onChange={(event) => setSignerTitle(event.target.value)} placeholder="Owner, Founder, CEO..." />
                </label>
                <label style={{ fontSize: 12, fontWeight: 700, color: T.text }}>
                  Signer Email
                  <input style={inputStyle} value={signerEmail} onChange={(event) => setSignerEmail(event.target.value)} />
                </label>
              </div>
              <label style={{
                display: 'grid',
                gridTemplateColumns: '18px minmax(0, 1fr)',
                gap: 10,
                alignItems: 'flex-start',
                marginTop: 14,
                borderRadius: 8,
                border: `1px solid ${T.border}`,
                padding: 12,
                color: T.textSub,
                fontSize: 12,
                lineHeight: 1.5,
              }}>
                <input
                  type="checkbox"
                  checked={acceptedTerms}
                  onChange={(event) => setAcceptedTerms(event.target.checked)}
                  style={{ marginTop: 2 }}
                />
                <span>
                  I confirm I am authorized to accept this agreement for {agreement.brandLegalName || clientBrand?.name || 'the brand'}, and I agree to the summary and full terms shown above.
                </span>
              </label>
              <button
                type="button"
                onClick={submitAcceptance}
                disabled={saving || !signerName.trim() || !signerTitle.trim() || !signerEmail.trim() || !acceptedTerms}
                style={{
                  marginTop: 14,
                  border: `1px solid ${T.gold}`,
                  background: T.gold,
                  color: '#0e0e0e',
                  borderRadius: 8,
                  padding: '9px 14px',
                  fontWeight: 800,
                  cursor: saving ? 'wait' : 'pointer',
                  opacity: saving ? 0.68 : 1,
                }}
              >
                {saving ? 'Accepting...' : 'Accept Agreement'}
              </button>
            </section>
          )}
        </div>
      )}
    </div>
  );
}

function Metric({ label, value, T }) {
  return (
    <div style={{ borderRadius: 8, border: `1px solid ${T.border}`, padding: 12 }}>
      <div style={{ fontSize: 10, textTransform: 'uppercase', letterSpacing: '0.08em', color: T.label, fontWeight: 800 }}>{label}</div>
      <div style={{ fontSize: 13, color: T.text, fontWeight: 750, marginTop: 5 }}>{value}</div>
    </div>
  );
}
